import React, { Key, memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { rigTableColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import { fetchEntityOptions } from '../../../widgets/static-data/api/api'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyRigTable } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryRigTablesTable = memo(
    ({
        parent,
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: InterventionsDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const [optionsOfRigTableEntity, setOptionsOfRigTableEntity] = useState(null)
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity,
            expandedRowKeys,
            setExpandedRowKeysByEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const rigTables = useMemo(
            () =>
                data.rigTables
                    .filter(rigT => rigT.parent == parent.nodeId)
                    .map(rigTable => ({
                        ...rigTable,
                        key: String(rigTable.rowId),
                        parent: parent.nodeId
                    })),
            [data.rigTables, parent.nodeId]
        )

        useEffect(() => {
            fetchEntityOptions('rigtable').then(data => setOptionsOfRigTableEntity(data))

            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const rigTable of data.rigTables) {
                newStartDate[rigTable.rowId] = rigTable.start ? new Date(rigTable.start) : ''
                newEndDate[rigTable.rowId] = rigTable.end ? new Date(rigTable.end) : ''
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        // Functions
        const handleEditData = useCallback(
            (entity: EntityDto[], row: EntityDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'RIG_TABLE').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setRigTables(newData as RigTableDto[])
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`rigTables=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: EntityDto): void => {
                handleEditData(data.rigTables, row, setData)
                // handleEditData(checkedData.rigTables, row, setCheckedData)
            },
            [data.rigTables, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.rigTables]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                currentRow.start = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.rigTables, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.rigTables]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                currentRow.end = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.rigTables, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.rigTables]

            const newEntity: RigTableDto = {
                ...emptyRigTable,
                type: 'Main',
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'RIG_TABLE').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setRigTables(oldEntity)
                goToNewRow()
            })
        }, [data.rigTables, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: EntityDto): void => {
                const newData = data.rigTables.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setRigTables(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [
                checkedRows,
                data.rigTables,
                formatCheckedRows,
                removeErrorWhenCheckedRowIsRemoved,
                setCheckedRows,
                setData
            ]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: RigTableDto): ReactElement => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: RigTableDto): ReactElement => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const rigTablesCols = useMemo(
            () =>
                rigTableColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfRigTableEntity,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.interventions,
                    localErrors
                ),
            [
                data.interventions,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                localErrors,
                moveRowToAnotherParent,
                optionsOfRigTableEntity
            ]
        )

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="rig tables" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'rigTables', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: RigTableDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'rigTables'
                    })
                },
                onSelect: (record: EntityDto, selected: boolean, selectedRows: RigTableDto[], nativeEvent: Event) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'rigTables',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const handleExpandedRowsChange = useCallback(
            (keys: React.Key[]): void => {
                setExpandedRowKeysByEntity('rigTables', keys as Key[])
            },
            [setExpandedRowKeysByEntity]
        )

        const expandable = useMemo(
            () => ({
                expandedRowKeys: expandedRowKeys?.rigTables,
                onExpandedRowsChange: handleExpandedRowsChange,
                defaultExpandAllRows: false
            }),
            [expandedRowKeys, handleExpandedRowsChange]
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={rigTables}
                    columns={rigTablesCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryRigTablesTable.displayName = 'TemporaryRigTablesTable'

export default TemporaryRigTablesTable

const TableContainer = styled.main``
